// services/claudeApi.js — Claude API Service via Worker proxy
// API key is in the Worker, never in the browser.
import { trackClaude } from './apiMonitor';
import { SYSTEM_PROMPT, COMPARISON_PROMPT, SCREENER_PROMPT, ALERT_SCANNER_PROMPT } from '../constants';

import { API_BASE } from '../config';
import { getAuthHeaders } from './workerClient';
import { netInvested } from '../components/portfolio/portfolioHelpers';

// OPT-1: Haiku for bulk reports (avoids Cloudflare 524 timeout on long outputs)
// Sonnet for analyst chat (single questions, shorter output, much better quality)
const MODEL_SONNET = 'claude-sonnet-4-6';
const MODEL_HAIKU  = 'claude-haiku-4-5-20251001';

// Current date in Italian — injected into all system prompts so Claude knows today's date
function todayIT() {
  return new Date().toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

// Retry with exponential backoff for rate limit errors (429 / "rate limit")
// Server-side Worker already retries + falls back to Haiku, so frontend retry is a safety net.
// Reduced to 1 retry (the Worker handles most retry logic now).
async function fetchWithRetry(url, options, maxRetries = 1) {
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    const res = await fetch(url, options);
    if (res.status === 429 && attempt < maxRetries) {
      const wait = 60000; // 60s — Worker already waited ~45s, give extra buffer
      console.log(`[Claude] Rate limited after server retries, frontend retry in ${wait / 1000}s...`);
      await new Promise(r => setTimeout(r, wait));
      continue;
    }
    const data = await res.json();
    if (data.error) {
      const msg = (data.error.message || data.error || '').toLowerCase();
      if (msg.includes('rate limit') && attempt < maxRetries) {
        const wait = 60000;
        console.log(`[Claude] Rate limited (body), frontend retry in ${wait / 1000}s...`);
        await new Promise(r => setTimeout(r, wait));
        continue;
      }
    }
    return data;
  }
  return { error: { message: 'Servizio AI temporaneamente sovraccarico. Riprova tra qualche minuto.' } };
}

// ── Skill Detection — priority: comparison > screener > alert ─────────────
function detectComparison(msg) {
  return /\bconfronta?\b|\bvs\.?\b|\bmeglio\s+\w+\s+o\s+\w+|\bcompar[ae]|\bscegli(?:ere)?\s+tra\b/i.test(msg);
}

function detectScreener(msg) {
  return /\btrova(?:mi)?\s+(?:titoli|azioni|stock)|\bscreener\b|\bmigliori\s+(?:titoli|azioni)|\bcerca\s+(?:titoli|azioni)|\bcon\s+(?:P\/E|dividend|yield|margine)\b|\bsotto\s+(?:P\/E|prezzo)/i.test(msg);
}

function detectAlert(msg) {
  return /\balert\b|\bsegnali\s+tecnic/i.test(msg);
}

// ── Portfolio Snapshot — injected in every analyst call ───────────────────
export function fmtPortfolioSnapshot(stocks, funds, watchlistQuotes) {
  if ((!stocks || stocks.length === 0) && (!funds || funds.length === 0)) return null;

  const lines = ['SNAPSHOT PORTAFOGLIO:'];

  // Calculate stock values + P&L
  let stockValue = 0;
  let stockEntryTotal = 0;
  const stockPnL = [];

  if (stocks && stocks.length > 0) {
    stocks.forEach(p => {
      const quote = watchlistQuotes?.[p.symbol];
      const price = quote?.c || 0;
      if (price > 0) {
        const value = price * p.qty;
        const entryValue = p.entry * p.qty;
        stockValue += value;
        stockEntryTotal += entryValue;
        const pnl = ((price - p.entry) / p.entry * 100);
        stockPnL.push({ symbol: p.symbol, pnl, value });
      } else {
        stockPnL.push({ symbol: p.symbol, pnl: null, value: null });
      }
    });
  }

  // Calculate fund values
  let fundValue = 0;
  let fundCount = 0;
  let fundNetInv = 0;
  let fundDividends = 0;

  if (funds && funds.length > 0) {
    funds.forEach(f => {
      fundValue += f.currentValue || 0;
      fundNetInv += netInvested(f);
      fundDividends += f.dividends || 0;
      fundCount++;
    });
  }

  const totalValue = stockValue + fundValue;

  // Asset allocation
  if (totalValue > 0) {
    const stockPct = (stockValue / totalValue * 100).toFixed(0);
    const fundPct = (fundValue / totalValue * 100).toFixed(0);
    // NB: i valori azionari NON sono convertiti in EUR (nessun tasso di cambio disponibile)
    lines.push(`Asset allocation: Azioni ${stockPct}% (${stockPnL.length} pos., val. ${Math.round(stockValue).toLocaleString('it-IT')} in valuta di quotazione, NON convertito) | Fondi ${fundPct}% (${fundCount} pos., val. €${Math.round(fundValue).toLocaleString('it-IT')})`);
  }

  // Concentration (HHI)
  const allPositions = [];
  stockPnL.forEach(s => { if (s.value) allPositions.push({ name: s.symbol, value: s.value }); });
  if (funds) funds.forEach(f => { if (f.currentValue > 0) allPositions.push({ name: (f.name || f.isin || '').substring(0, 15), value: f.currentValue }); });

  if (allPositions.length > 0 && totalValue > 0) {
    allPositions.sort((a, b) => b.value - a.value);
    const top3 = allPositions.slice(0, 3).map(p => `${p.name} ${(p.value / totalValue * 100).toFixed(0)}%`).join(', ');
    const hhi = allPositions.reduce((sum, p) => { const w = p.value / totalValue; return sum + w * w; }, 0);
    const hhiLabel = hhi > 0.25 ? 'alta' : hhi > 0.15 ? 'moderata' : 'bassa';
    lines.push(`Concentrazione: Top3 = ${top3} (HHI ${hhiLabel})`);
  }

  // Stock P&L
  if (stockPnL.length > 0) {
    const parts = stockPnL.map(s => {
      if (s.pnl === null) return `${s.symbol} n/d`;
      const emoji = s.pnl > 2 ? '✅' : s.pnl < -2 ? '❌' : '➖';
      return `${s.symbol} ${s.pnl >= 0 ? '+' : ''}${s.pnl.toFixed(1)}% ${emoji}`;
    });
    if (stockEntryTotal > 0) {
      const totalPnl = ((stockValue - stockEntryTotal) / stockEntryTotal * 100).toFixed(1);
      parts.push(`Totale: ${totalPnl >= 0 ? '+' : ''}${totalPnl}%`);
    }
    lines.push(`P&L azioni: ${parts.join(' | ')}`);
  }

  // Fund P&L
  if (fundCount > 0 && fundNetInv > 0) {
    const fundReturn = ((fundValue - fundNetInv + fundDividends) / fundNetInv * 100).toFixed(1);
    lines.push(`P&L fondi: rend.tot ${fundReturn >= 0 ? '+' : ''}${fundReturn}% (inv.netto €${Math.round(fundNetInv).toLocaleString('it-IT')} → controv. €${Math.round(fundValue).toLocaleString('it-IT')}${fundDividends > 0 ? ` + prov. €${Math.round(fundDividends).toLocaleString('it-IT')}` : ''})`);
  }

  // Quick alerts from watchlist (daily change > 5%)
  const quickAlerts = [];
  if (stocks && watchlistQuotes) {
    stocks.forEach(p => {
      const quote = watchlistQuotes[p.symbol];
      if (quote && Math.abs(quote.dp) > 5) {
        quickAlerts.push(`${p.symbol} ${quote.dp > 0 ? '+' : ''}${quote.dp.toFixed(1)}%`);
      }
    });
  }
  if (quickAlerts.length > 0) lines.push(`⚡ Alert: ${quickAlerts.join(' | ')}`);

  return lines.join('\n');
}

// ── Alert Summary — for alert scanner skill context ──────────────────────
export function fmtAlertSummary(alerts) {
  if (!alerts || alerts.length === 0) return null;
  const emojiMap = { positive: '🟢', negative: '🔴', warning: '🟡', info: '🔵' };
  const lines = [`ALERT ATTIVI (${alerts.length} segnali):`];
  alerts.forEach(a => {
    lines.push(`${emojiMap[a.type] || '⚪'} ${a.title} — ${a.body} [priority:${a.priority}]`);
  });
  return lines.join('\n');
}

export async function callClaude(messages, profileCtx, portfolioCtx, snapshotCtx, alertCtx) {
  const sys = [`DATA DI OGGI: ${todayIT()}. Usa questa data come riferimento per tutte le analisi, piani e raccomandazioni.\n\n` + SYSTEM_PROMPT];
  if (profileCtx) sys.push('\nPROFILO INVESTITORE:\n' + profileCtx);
  if (portfolioCtx) sys.push('\nPORTAFOGLIO ATTUALE:\n' + portfolioCtx);
  if (snapshotCtx) sys.push('\n' + snapshotCtx);

  // Skill detection — append specialized prompt if triggered
  const lastMsg = messages[messages.length - 1]?.content || '';
  const isComparison = detectComparison(lastMsg);
  const isScreener = !isComparison && detectScreener(lastMsg);
  const isAlert = !isComparison && !isScreener && detectAlert(lastMsg);

  if (isComparison)       sys.push('\n' + COMPARISON_PROMPT);
  else if (isScreener)    sys.push('\n' + SCREENER_PROMPT);
  else if (isAlert) {
    sys.push('\n' + ALERT_SCANNER_PROMPT);
    if (alertCtx) sys.push('\n' + alertCtx);
  }

  try {
    const data = await fetchWithRetry(API_BASE + '/api/claude', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', ...getAuthHeaders() },
      body: JSON.stringify({
        model: MODEL_SONNET,
        max_tokens: 2048,
        temperature: 0.3,
        system: sys.join('\n'),
        messages,
        tools: [{ type: 'web_search_20250305', name: 'web_search' }],
      }),
    });

    if (data.error) {
      trackClaude({}, false, true);
      return { error: data.error.message || data.error || 'Errore API Claude' };
    }

    const usage = data.usage || { input_tokens: 0, output_tokens: 0 };
    const hasWebSearch = (data.content || []).some(
      b => b.type === 'tool_use' || b.type === 'web_search_tool_result'
    );

    trackClaude(usage, hasWebSearch, false);

    const text = (data.content || [])
      .filter(b => b.type === 'text')
      .map(b => b.text)
      .join('\n');

    return { text: text || 'Nessuna risposta ricevuta.', usage };
  } catch (e) {
    trackClaude({}, false, true);
    return { error: e.message || 'Errore di connessione a Claude' };
  }
}

export function fmtProfile(p) {
  if (!p) return null;
  const lines = [
    `- Profilo rischio: ${p.label} ${p.emoji || ""}`,
    `- Fascia d'età: ${p.age || "non specificata"}`,
    `- Reddito annuo: ${p.income || "non specificato"}`,
    `- Risparmio mensile: ${p.savings || "non specificato"}`,
    `- Capitale investibile: ${p.capital || "non specificato"}`,
    `- Orizzonte temporale: ${p.horizon || "non specificato"}`,
    `- Obiettivo: ${p.goal || "non specificato"}`,
    `- Esperienza: ${p.experience || "non specificata"}`,
    `- Consapevolezza rischio: ${p.knowledgeCheck || "non specificata"}`,
    `- Scenario -20%: ${p.scenarioDrop || "non specificato"}`,
    `- Preferenza rischio/rendimento: ${p.scenarioGamble || "non specificata"}`,
    `- Perdita max accettabile: ${p.maxLoss || "non specificata"}`,
    `- Preferenze ESG: ${p.esg || "non specificate"}`,
    `- Settori preferiti: ${p.sectors || "nessuna preferenza"}`,
  ];
  if (p.allocation) {
    const alloc = Object.entries(p.allocation)
      .filter(([_, v]) => v > 0)
      .map(([k, v]) => `${k}: ${v}%`)
      .join(", ");
    lines.push(`- Allocazione target: ${alloc}`);
  }
  lines.push(`- Max posizione singola: ${p.maxPosition || 20}%`);
  lines.push(`- Stop loss default: -${p.stopLoss || 10}%`);
  return lines.join("\n");
}

export function fmtPortfolio(pos) {
  if (!pos || pos.length === 0) return null;
  return pos.map(p => p.symbol + ': ' + p.qty + ' azioni @ $' + p.entry + ', stop $' + p.stopLoss).join('\n');
}

// ── Full Portfolio Formatter (stocks + funds) — used by Intelligence Chat ────
export function fmtFullPortfolio(stocks, funds) {
  const sections = [];

  // Stocks section
  if (stocks && stocks.length > 0) {
    const stockLines = stocks.map(p => `- ${p.symbol}: ${p.qty} azioni @ $${p.entry}, stop $${p.stopLoss}`);
    sections.push(`AZIONI (${stocks.length} posizioni):\n${stockLines.join('\n')}`);
  } else {
    sections.push('AZIONI: nessuna posizione');
  }

  // Funds section
  if (funds && funds.length > 0) {
    const totalInvested = funds.reduce((s, f) => s + f.investedAmount, 0);
    const totalValue = funds.reduce((s, f) => s + f.currentValue, 0);
    const totalDividends = funds.reduce((s, f) => s + (f.dividends || 0), 0);
    const medio = funds.filter(f => f.horizon === 'medio');
    const lungo = funds.filter(f => f.horizon === 'lungo');

    const fmtFund = (f) => {
      const pct = f.investedAmount > 0 ? ((f.currentValue - f.investedAmount) / f.investedAmount * 100).toFixed(1) : '0';
      const divStr = f.dividends > 0 ? `, proventi €${f.dividends.toLocaleString('it-IT')}` : '';
      return `- ${f.name} (${f.isin}): ${f.shares.toFixed(2)} quote, versato €${f.investedAmount.toLocaleString('it-IT')}, controv. €${f.currentValue.toLocaleString('it-IT')}, ${pct >= 0 ? '+' : ''}${pct}%${divStr}`;
    };

    let fundText = `FONDI COMUNI (${funds.length} posizioni, totale versato €${totalInvested.toLocaleString('it-IT')}, controvalore €${totalValue.toLocaleString('it-IT')}, proventi totali €${totalDividends.toLocaleString('it-IT')}):`;
    if (medio.length > 0) {
      fundText += `\nMedio termine (${medio.length}):\n${medio.map(fmtFund).join('\n')}`;
    }
    if (lungo.length > 0) {
      fundText += `\nLungo termine (${lungo.length}):\n${lungo.map(fmtFund).join('\n')}`;
    }
    sections.push(fundText);
  } else {
    sections.push('FONDI COMUNI: nessuna posizione');
  }

  return sections.join('\n\n');
}

// ── Compact Portfolio Formatter — OPT-7: for Intelligence report (~60% fewer tokens) ────
// Suffisso di borsa → valuta di quotazione. Serve a non stampare "$" su titoli europei.
const EXCHANGE_CCY = {
  MI: 'EUR', PA: 'EUR', DE: 'EUR', F: 'EUR', AS: 'EUR', BR: 'EUR', MC: 'EUR',
  LS: 'EUR', VI: 'EUR', IR: 'EUR', HE: 'EUR',
  L: 'GBP', SW: 'CHF', TO: 'CAD', HK: 'HKD', T: 'JPY', ST: 'SEK', OL: 'NOK', CO: 'DKK',
};
const ccyOf = (symbol = '') => {
  const dot = symbol.lastIndexOf('.');
  if (dot === -1) return 'USD';
  return EXCHANGE_CCY[symbol.slice(dot + 1).toUpperCase()] || 'USD';
};

export function fmtCompactPortfolio(stocks, funds, quotes) {
  const lines = [];
  const notes = [];
  const stockByCcy = {};
  const noPrice = [];

  // ── Azioni: prezzo corrente, controvalore, valuta, distanza dallo stop ──
  if (stocks && stocks.length > 0) {
    lines.push(`AZIONI (${stocks.length}):`);
    lines.push('Ticker|Qty|Carico|PrezzoOggi|Controvalore|Valuta|P&L%|Stop|DistStop%');
    stocks.forEach(p => {
      const ccy = ccyOf(p.symbol);
      const price = quotes?.[p.symbol]?.c || 0;
      const has = price > 0;
      const value = has ? price * p.qty : null;
      const pnlPct = has && p.entry > 0 ? ((price - p.entry) / p.entry) * 100 : null;
      const distStop = has && p.stopLoss > 0 ? ((price - p.stopLoss) / price) * 100 : null;
      if (has) stockByCcy[ccy] = (stockByCcy[ccy] || 0) + value;
      else noPrice.push(p.symbol);
      lines.push([
        p.symbol, p.qty, p.entry,
        has ? price.toFixed(2) : 'n.d.',
        has ? Math.round(value) : 'n.d.',
        ccy,
        pnlPct != null ? pnlPct.toFixed(1) : 'n.d.',
        p.stopLoss || 'n.d.',
        distStop != null ? distStop.toFixed(1) : 'n.d.',
      ].join('|'));
    });
    const ccyList = Object.entries(stockByCcy).map(([c, v]) => `${c} ${Math.round(v).toLocaleString('it-IT')}`);
    if (ccyList.length > 0) lines.push(`Subtotale azioni per valuta: ${ccyList.join(' + ')}`);
    if (ccyList.length > 1) notes.push('Le azioni sono in valute diverse e NON e\' disponibile alcun tasso di cambio: non sommare tra valute e non calcolare un totale patrimoniale unico.');
    if (noPrice.length > 0) notes.push(`Prezzo corrente NON disponibile per: ${noPrice.join(', ')}. Per queste posizioni non e' calcolabile ne' controvalore ne' P&L: dichiaralo, non stimarlo.`);
    notes.push('I ticker NON sono accompagnati da denominazione sociale ne\' da ISIN: l\'identita\' dell\'emittente e\' NON VERIFICATA. Non dedurla dal ticker e non attribuire settore o business a una posizione azionaria; se serve, dillo esplicitamente.');
  }

  // ── Fondi: % su versato LORDO (convenzione Mediolanum), asset class esplicita ──
  if (funds && funds.length > 0) {
    const totalGross = funds.reduce((s, f) => s + (f.investedAmount || 0), 0);
    const totalInv = funds.reduce((s, f) => s + netInvested(f), 0);
    const totalVal = funds.reduce((s, f) => s + (f.currentValue || 0), 0);
    const totalDiv = funds.reduce((s, f) => s + (f.dividends || 0), 0);
    const totalPct = totalGross > 0 ? ((totalVal - totalInv + totalDiv) / totalGross * 100).toFixed(1) : '0';
    lines.push(`\nFONDI (${funds.length}, versato lordo €${Math.round(totalGross).toLocaleString('it-IT')}, inv.netto €${Math.round(totalInv).toLocaleString('it-IT')}, controv. €${Math.round(totalVal).toLocaleString('it-IT')}, proventi €${Math.round(totalDiv).toLocaleString('it-IT')}, rend.tot ${totalPct}%):`);
    lines.push('Nome|ISIN|AssetClass|Orizzonte|Lordo€|Inv.Netto€|Controv€|Conto Cap%|Proventi€|Rend.Tot%|Peso%Fondi');

    funds.forEach(f => {
      const ni = netInvested(f);
      const gross = f.investedAmount || 0;
      const val = f.currentValue || 0;
      const capPct = gross > 0 ? ((val - ni) / gross * 100).toFixed(1) : '0';
      const totPct = gross > 0 ? ((val - ni + (f.dividends || 0)) / gross * 100).toFixed(1) : '0';
      const weight = totalVal > 0 ? (val / totalVal * 100).toFixed(1) : '0';
      lines.push(`${f.name}|${f.isin}|${f.assetClass || 'NON CLASSIFICATO'}|${f.horizon}|${Math.round(gross)}|${Math.round(ni)}|${Math.round(val)}|${capPct}|${Math.round(f.dividends || 0)}|${totPct}|${weight}`);
    });

    notes.push('"Peso%Fondi" e\' la quota sul solo totale FONDI, NON sul patrimonio complessivo. Non usarlo come peso patrimoniale.');
    if (funds.some(f => !f.assetClass)) notes.push('AssetClass "NON CLASSIFICATO" significa dato ASSENTE. Non dedurre la classe di attivo dal nome del fondo e non calcolare split azioni/obbligazioni: dichiara che il dato manca.');
    notes.push('Le percentuali dei fondi sono calcolate su versato LORDO (convenzione Mediolanum), coerenti con la UI dell\'app.');
  }

  if (notes.length > 0) {
    lines.push('\nLIMITI DEI DATI (vincolanti):');
    notes.forEach((n, i) => lines.push(`${i + 1}. ${n}`));
  }

  return lines.join('\n');
}

// ── Fatti strutturali calcolati in codice (deterministici, mai delegati al modello) ──────
// Concentrazione per casa prodotto, classi duplicate, sovrapposizioni tematiche e
// scenari di riduzione sono aritmetica: se li calcola l'LLM puo' sbagliarli.
const PRODUCT_HOUSE = [
  [/^(SMFI|MBB|Challenge)\b|Mediolanum/i, 'Mediolanum'],
  [/^Pictet/i, 'Pictet'],
  [/^CPR Invest/i, 'Amundi (CPR)'],
  [/^(Amundi|Lyxor)/i, 'Amundi'],
  [/^(iShares|BlackRock)/i, 'BlackRock'],
  [/^Vanguard/i, 'Vanguard'],
  [/^(Fidelity|Schroder|JPM|Morgan Stanley|Invesco|Franklin|Nordea|BNP|Axa|Allianz|DWS|UBS)/i, '$1'],
];
const houseOf = (name = '') => {
  for (const [re, label] of PRODUCT_HOUSE) {
    const m = name.match(re);
    if (m) return label === '$1' ? m[1] : label;
  }
  return (name.split(/[\s-]+/)[0] || 'n.d.');
};
// Nome "base" del fondo, senza classe/contratto: serve a scoprire classi multiple dello stesso prodotto
const baseFundName = (name = '') => name
  .replace(/\bn\.\s*[\w/]+\s*$/i, '')
  .replace(/\b(L|LA|LB|LHB|LH|R|A|HR|I|B)\s+(EUR|USD)?\s*(PIC|PAC)?\s*$/i, '')
  .replace(/\b(EUR|USD)\b|\b(PIC|PAC)\b|\bAcc\b|\bDis\b|\bClass\b/gi, '')
  .replace(/\s{2,}/g, ' ').trim().toLowerCase();

// Etichetta per un prodotto aggregato: via il numero di contratto E il token di classe
const aggLabel = (name = '') => name
  .replace(/\bn\.\s*[\w/]+\s*$/i, '')
  .replace(/\b(LHB|LB|LA|LH|HR|L|R|A|I|B)\s+(EUR|USD)\s*(PIC|PAC)?\s*$/i, '')
  .trim();

const THEMES = ['robotic', 'disruptive', 'innovat', 'thematic', 'technolog', 'healthcare', 'energy',
  'infrastructure', 'water', 'circular', 'demographic', 'india', 'china', 'leaders', 'coupon'];

export function fmtStructuralFacts(stocks, funds, quotes) {
  const lines = [];
  const fundVal = (f) => f.currentValue || 0;
  const fundsTotal = (funds || []).reduce((s, f) => s + fundVal(f), 0);

  // Azioni valutate solo se in EUR e con prezzo: il totale patrimoniale deve restare monovaluta
  let eurStocks = 0;
  const nonEur = [];
  const stockPos = [];
  (stocks || []).forEach(p => {
    const ccy = ccyOf(p.symbol);
    const price = quotes?.[p.symbol]?.c || 0;
    if (price <= 0) return;
    const v = price * p.qty;
    if (ccy === 'EUR') { eurStocks += v; stockPos.push({ label: p.symbol, value: v, qty: p.qty, price }); }
    else nonEur.push(`${p.symbol} (${ccy})`);
  });

  const totalEur = fundsTotal + eurStocks;
  if (totalEur <= 0) return '';

  lines.push('FATTI STRUTTURALI (calcolati dall\'app, NON ricalcolarli):');
  lines.push(`Base patrimoniale EUR = fondi €${Math.round(fundsTotal).toLocaleString('it-IT')} + azioni EUR €${Math.round(eurStocks).toLocaleString('it-IT')} = €${Math.round(totalEur).toLocaleString('it-IT')}`);
  if (nonEur.length > 0) lines.push(`ESCLUSE dalla base (valuta diversa, nessun cambio disponibile): ${nonEur.join(', ')}. I pesi sotto NON le includono.`);

  // 1. Concentrazione per casa prodotto
  const byHouse = {};
  (funds || []).forEach(f => { const h = houseOf(f.name); byHouse[h] = (byHouse[h] || 0) + fundVal(f); });
  const houses = Object.entries(byHouse).sort((a, b) => b[1] - a[1]);
  if (houses.length > 0) {
    lines.push(`\nConcentrazione per casa prodotto (sui soli fondi, €${Math.round(fundsTotal).toLocaleString('it-IT')}):`);
    houses.forEach(([h, v]) => {
      const n = (funds || []).filter(f => houseOf(f.name) === h).length;
      lines.push(`- ${h}: ${n} fondi, €${Math.round(v).toLocaleString('it-IT')} = ${(v / fundsTotal * 100).toFixed(1)}%`);
    });
  }

  // 2. Classi multiple dello stesso prodotto
  const byBase = {};
  (funds || []).forEach(f => {
    const k = baseFundName(f.name);
    if (!k) return;
    (byBase[k] = byBase[k] || []).push(f);
  });
  const dupes = Object.entries(byBase).filter(([, arr]) => arr.length > 1);
  if (dupes.length > 0) {
    lines.push('\nClassi multiple dello STESSO prodotto (verificare differenza di costo e copertura valutaria):');
    dupes.forEach(([, arr]) => {
      lines.push(`- ${arr.map(f => `${f.name} (${f.isin}, €${Math.round(fundVal(f)).toLocaleString('it-IT')})`).join(' | ')}`);
    });
  }

  // 2-bis. Esposizione AGGREGATA per prodotto: piu' classi dello stesso fondo nascondono
  // la concentrazione reale, perche' ogni singola classe puo' restare sotto soglia.
  const aggregated = Object.entries(byBase)
    .filter(([, arr]) => arr.length > 1)
    .map(([, arr]) => ({
      label: aggLabel(arr[0].name),
      value: arr.reduce((s, f) => s + fundVal(f), 0),
      classes: arr.length,
      isins: arr.map(f => f.isin),
    }))
    .filter(p => p.value / totalEur > 0.05)
    .sort((a, b) => b.value - a.value);
  if (aggregated.length > 0) {
    lines.push('\nEsposizione AGGREGATA per prodotto (classi sommate) — la concentrazione reale:');
    aggregated.forEach(p => {
      lines.push(`- ${p.label}: ${p.classes} classi (${p.isins.join(', ')}), totale €${Math.round(p.value).toLocaleString('it-IT')} = ${(p.value / totalEur * 100).toFixed(1)}% della base EUR`);
      lines.push('    ATTENZIONE: guardando le singole classi questa concentrazione NON e\' visibile. Trattala come una posizione unica.');
    });
  }

  // 3. Possibile sovrapposizione tematica (indizio da nome: da confermare con look-through)
  const thematic = (funds || []).filter(f => THEMES.some(t => (f.name || '').toLowerCase().includes(t)));
  if (thematic.length > 1) {
    const tv = thematic.reduce((s, f) => s + fundVal(f), 0);
    lines.push(`\nFondi con denominazione tematica/settoriale: ${thematic.length} su ${(funds || []).length}, €${Math.round(tv).toLocaleString('it-IT')} = ${(tv / totalEur * 100).toFixed(1)}% della base EUR.`);
    lines.push('INDIZIO da nome, NON prova di sovrapposizione: serve look-through delle prime partecipazioni. Elencali e di\' che va verificato.');
  }

  // 4. Scenari di riduzione per posizioni sopra il 10% — pura aritmetica
  // I prodotti con piu' classi entrano AGGREGATI: ridurre una sola classe non riduce l'esposizione reale.
  const fundPositions = Object.values(byBase).map(arr => arr.length === 1
    ? { label: `${arr[0].name} (${arr[0].isin})`, value: fundVal(arr[0]), unit: 'quote', qty: arr[0].shares }
    : {
      label: `${aggLabel(arr[0].name)} — ${arr.length} classi aggregate (${arr.map(f => f.isin).join(', ')})`,
      value: arr.reduce((s, f) => s + fundVal(f), 0), unit: null, qty: null,
    });

  const positions = [
    ...fundPositions,
    ...stockPos.map(p => ({ label: p.label, value: p.value, unit: 'azioni', qty: p.qty, price: p.price })),
  ].filter(p => p.value / totalEur > 0.10).sort((a, b) => b.value - a.value);

  if (positions.length > 0) {
    lines.push('\nScenari di riduzione (ipotesi: il ricavato resta in portafoglio come liquidita\', quindi la base EUR non cambia):');
    positions.forEach(p => {
      const w = p.value / totalEur * 100;
      const rows = [15, 10, 5].filter(t => t < w).map(t => {
        const sell = p.value - (t / 100) * totalEur;
        const frac = sell / p.value;
        const units = p.qty ? ` ≈ ${(p.qty * frac).toFixed(p.unit === 'azioni' ? 0 : 2)} ${p.unit}` : '';
        return `per scendere al ${t}%: vendere €${Math.round(sell).toLocaleString('it-IT')} (${(frac * 100).toFixed(0)}% della posizione)${units}`;
      });
      lines.push(`- ${p.label}: peso attuale ${w.toFixed(1)}%, valore €${Math.round(p.value).toLocaleString('it-IT')}`);
      rows.forEach(r => lines.push(`    ${r}`));
      lines.push('    Plus/minusvalenza FISCALE di questi scenari: NON calcolabile (manca il prezzo di carico fiscale). Dichiaralo.');
    });
  }

  return lines.join('\n');
}

// ── Compact Previous Report — OPT-8: extract only actionable sections (~70% fewer tokens) ────
export function fmtCompactPreviousReport(reportText, reportDate) {
  if (!reportText) return '';
  const dateStr = reportDate ? new Date(reportDate).toLocaleDateString('it-IT') : '?';

  // Extract key sections only: urgent actions + top 3 + position table
  const sections = [];
  const extractSection = (text, header) => {
    const regex = new RegExp(`${header}[\\s\\S]*?(?=##|$)`, 'i');
    const match = text.match(regex);
    return match ? match[0].trim().substring(0, 500) : null; // cap each section
  };

  const urgent = extractSection(reportText, '## 🔴 AZIONI URGENTI');
  const top3 = extractSection(reportText, '## 💡 TOP 3');
  const trend = extractSection(reportText, '## 📉 TREND TEMPORALE');

  if (urgent) sections.push(urgent);
  if (top3) sections.push(top3);
  if (trend) sections.push(trend);

  if (sections.length === 0) {
    // Fallback: first 800 chars
    sections.push(reportText.substring(0, 800));
  }

  return `\nREPORT PRECEDENTE (${dateStr}) — sintesi:\n${sections.join('\n\n')}\nConfronta: posizioni migliorate/peggiorate, esito raccomandazioni precedenti.\n`;
}

// ── Recovery Data Parser ─────────────────────────────────────
export function parseRecoveryData(reportText) {
  if (!reportText) return null;
  const match = reportText.match(/<!--RECOVERY_DATA:(.*?)-->/s);
  if (!match) return null;
  try {
    return JSON.parse(match[1]);
  } catch (e) {
    console.warn('Failed to parse RECOVERY_DATA:', e);
    return null;
  }
}

export function stripRecoveryData(reportText) {
  if (!reportText) return reportText;
  return reportText.replace(/\n?<!--RECOVERY_DATA:.*?-->/s, '').trim();
}

// ── Intelligence Agent ───────────────────────────────────────
export async function callClaudeIntelligence(profileCtx, stocks, funds, previousReport, quotes) {
  // OPT-7: Compact portfolio format (~60% fewer tokens than full format)
  const compactPortfolio = fmtCompactPortfolio(stocks, funds, quotes);

  // OPT-8: Compact previous report (only actionable sections, ~70% fewer tokens)
  const deltaSection = previousReport?.text
    ? fmtCompactPreviousReport(previousReport.text, previousReport.date)
    : '';

  // Fatti strutturali calcolati in codice: il modello li interpreta, non li ricalcola
  const structuralFacts = fmtStructuralFacts(stocks, funds, quotes);

  // OPT-9: Batched web search strategy — 5-6 grouped searches instead of 18-25 individual
  const prompt = `Dossier istruttorio sul patrimonio, destinato a preparare un colloquio con un consulente umano.
Obiettivo: fornire al lettore basi oggettive, scenari e opzioni con controtesi — NON istruzioni operative.
I dati sono in formato tabellare compatto (separatore |).

PATRIMONIO:
${compactPortfolio}

${structuralFacts}
${deltaSection}
ISTRUZIONI:
1. WEB SEARCH EFFICIENTE — raggruppa le ricerche per massimizzare info con minime query:
   a) Ricerca MACRO: "BCE tassi inflazione outlook" + "S&P500 MSCI World bond market trend"
   b) Ricerca per SETTORE/ASSET CLASS (non per singolo fondo): "European bond funds performance", "Italian equity funds outlook", "global equity funds trend"
   c) Ricerca SINGOLA solo per posizioni problematiche (perdita >5%) o top 3 per peso
   d) MASSIMO 6-8 web search totali. Mai 1 ricerca per fondo.
2. Rispetta il profilo investitore (perdita max, orizzonte, rischio).
3. RENDIMENTO TOTALE = (controvalore - versato netto + proventi) / versato LORDO (convenzione Mediolanum, coerente con la UI). Mostra sempre conto capitale E rendimento totale. Raccomandazioni basate su rendimento totale. NON ricalcolare le percentuali: usa quelle fornite in tabella.
4. RECUPERO posizioni in perdita: breve <6m, medio 6-18m, lungo >18m. Motivazione specifica.
5. RACCOMANDAZIONI CONCRETE: cosa fare + quanto + quando + perche'. Mai "monitorare" senza soglie. ECCEZIONE VINCOLANTE: se manca un dato necessario (vedi LIMITI DEI DATI), NON stimarlo e NON inventarlo — scrivi "dati insufficienti" e indica quale dato serve. Una raccomandazione basata su un numero inventato e' un errore grave; l'assenza di raccomandazione non lo e'.
5-bis. COERENZA ARITMETICA: prima di scrivere un importo o una percentuale, verifica che le somme tornino. Se un totale non quadra con i suoi addendi, segnala l'incoerenza invece di pubblicarla. Non contare due volte la stessa posizione.
5-ter. DIMENSIONAMENTO: per proporre un acquisto di importo X devi conoscere il prezzo corrente del titolo. numero azioni = X / prezzo. Se il prezzo e' "n.d.", non proporre importi ne' quantita'.
6. NOMI CHIARI: quando consigli azioni su fondi, SEMPRE usa "Nome Fondo (ISIN)" — es. "Amundi MSCI World (LU1234567890)". Mai descrizioni generiche come "fondi europei" o "bond corporate". Ogni azione deve essere univocamente identificabile dal lettore.
7. FORMATO OPZIONE (sostituisce il vecchio formato "azione"): il lettore NON eseguira' il report, lo portera' a un consulente. Ogni opzione deve avere: (a) COSA si potrebbe valutare, con il fondo o titolo identificato da nome+ISIN/ticker; (b) PERCHE', con il dato oggettivo che la motiva; (c) CONTROTESI obbligatoria — cosa renderebbe questa opzione sbagliata, in 1-2 righe concrete, mai generica; (d) DA VERIFICARE col consulente — quali dati mancano per decidere. Se non riesci a scrivere una controtesi sensata, l'opzione e' probabilmente infondata: eliminala.
7-bis. CONFIDENZA: marca ogni affermazione non banale con [confidenza: alta|media|bassa]. "Alta" solo se poggia su un dato in tabella o su una fonte web citata con data. Le opinioni senza fonte sono "bassa".
7-ter. COSTI: cerca il TER/spese correnti dei fondi con peso maggiore (KIID/scheda prodotto). Segnala le strutture a doppio strato di costo (fondo-di-fondi o multi-manager che delega a gestori terzi). Se il TER non si trova, scrivi "TER non reperito" — NON stimarlo.
7-quater. DOMANDE PER IL CONSULENTE: chiudi con 5-8 domande precise, ognuna con il numero rilevante gia' inserito, in modo che siano poste senza ulteriore preparazione. Includi le domande scomode: concentrazione su un'unica casa prodotto, costi a doppio strato, classi multiple dello stesso fondo, alternative a basso costo, e conflitto di interessi nel collocamento.
8. TREND TEMPORALE: 3 orizzonti (breve 0-3m, medio 3-12m, lungo 1-3a), basato su macro+settori+tecnici.
9. LIMITI DEI DATI — la sezione omonima nel blocco PATRIMONIO e' VINCOLANTE e prevale su ogni altra istruzione. In particolare: (a) l'identita' societaria di un ticker azionario NON e' verificata: non dedurre azienda, settore o business dal ticker, e non costruire tesi su di essi; (b) non sommare valori in valute diverse e non produrre un totale patrimoniale unico se le valute differiscono; (c) se l'AssetClass di un fondo e' "NON CLASSIFICATO" il dato e' assente: non dedurla dal nome e non calcolare split azioni/obbligazioni; (d) "Peso%Fondi" e' quota sul totale fondi, non sul patrimonio.
10. STOP LOSS: uno stop si attiva quando il prezzo SCENDE sotto il livello. Uno stop sopra il prezzo di carico e' normale (trailing stop a protezione del profitto), non un errore. Usa "DistStop%": se positivo lo stop e' sotto il prezzo, quindi NON violato. Non proporre di abbassare uno stop senza una motivazione di rischio esplicita.

FORMATO:
${deltaSection ? `## 🔄 COSA È CAMBIATO\n` : ''}## 📊 QUADRO PATRIMONIALE
[Solo fatti, dalle tabelle. Dichiara i limiti dei dati che ti impediscono conclusioni.]

## 🌍 SCENARI MACRO
[2-3 scenari: base, avverso, favorevole. Per ognuno: driver principali, probabilita' QUALITATIVA (alta/media/bassa, mai numeri percentuali inventati), cosa implicherebbe per le classi di attivo effettivamente presenti in portafoglio, fonte con data. Non un bollettino generico: collega ogni scenario alle posizioni reali.]

## 📉 TREND PER CLASSE E SETTORE
| Ambito | Tendenza | Driver | Orizzonte | Fonte (data) | Confidenza |

## 🔍 OSSERVAZIONI PER POSIZIONE
| Posizione (ISIN/ticker) | Conto Cap. | Rend. Tot. | Osservazione | Confidenza |
[Osservazioni, non ordini. Includi il recupero stimato dove pertinente.]

## ⚠️ RISCHI STRUTTURALI
[Usa i FATTI STRUTTURALI forniti: concentrazione per casa prodotto, classi multiple, tematici, valute escluse. Numeri, non giudizi. Aggiungi l'esposizione geografica complessiva se ricavabile, dichiarando cosa non e' verificabile senza look-through.]

## 💰 COSTI
[TER/spese correnti dei fondi principali con fonte. Strutture a doppio strato. "TER non reperito" dove non trovato.]

## 🧭 OPZIONI SUL TAVOLO
[Per ciascuna: COSA · PERCHE' (dato oggettivo) · CONTROTESI · DA VERIFICARE. Per le posizioni sopra soglia riporta gli scenari di riduzione GIA' CALCOLATI nei fatti strutturali, senza ricalcolarli. Mai un'opzione senza controtesi.]

## ❓ DOMANDE PER IL CONSULENTE
[5-8 domande precise, con i numeri gia' inseriti, incluse quelle scomode su costi, concentrazione su un'unica casa prodotto e conflitto di interessi nel collocamento.]

## 📚 FONTI
[Elenco con data di pubblicazione. Se una sezione non ha fonti, dillo.]

⚠️ DISCLAIMER: Analisi informativa e non consulenza finanziaria ai sensi del D.Lgs. 58/1998 (TUF). Materiale predisposto per essere discusso con un consulente abilitato: le opzioni non sono raccomandazioni e i dimensionamenti fiscali non sono calcolati.

DATI STRUTTURATI — alla FINE, dopo il disclaimer, aggiungi OBBLIGATORIAMENTE:
<!--RECOVERY_DATA:{"ISIN":{"horizon":"breve|medio|lungo|positivo","confidence":"alta|media|bassa","note":"spiegazione"},...}-->
Includi TUTTE le posizioni. "positivo" = rendimento totale >= 0.`;

  const sys = [
    `DATA DI OGGI: ${todayIT()}. Usa questa data come riferimento per tutte le analisi, piani e raccomandazioni.`,
    'Sei il Portfolio Intelligence Agent. Produci un DOSSIER ISTRUTTORIO che il lettore portera\' a un consulente finanziario umano: il tuo scopo e\' dargli basi oggettive, scenari e opzioni con controtesi, non istruzioni da eseguire. Italiano. Pragmatico e diretto. Mai rendimenti garantiti.',
    'Il lettore sa che il suo consulente lavora per una banca e ha un potenziale conflitto di interessi nel collocamento. Il tuo valore e\' fornirgli elementi verificabili e domande scomode ma legittime, non sostituirsi a lui.',
    'REGOLE: (1) web_search per macro e settori, con fonte e data. (2) Rispetta il profilo investitore. (3) Rendimento totale include proventi. (4) Ogni opzione = COSA + PERCHE\' (dato oggettivo) + CONTROTESI + DA VERIFICARE; nessuna opzione senza controtesi. (5) NOMI: sempre "Nome Fondo (ISIN)", mai descrizioni generiche. (6) Marca le affermazioni con [confidenza: alta|media|bassa]. (7) Chiudi con domande per il consulente, numeri inclusi.',
    'INTEGRITA\' DEI DATI (prevale su tutto): non inventare mai un numero che non e\' nei dati forniti. Se un dato manca, scrivi "dati insufficienti" e di\' quale dato serve — e\' una risposta accettabile e preferibile a una stima. Non dedurre l\'identita\' o il settore di un\'azienda dal solo ticker. Non sommare valute diverse. Verifica che le somme tornino prima di pubblicarle. La sezione "LIMITI DEI DATI" nel messaggio utente e\' vincolante.',
  ].join('\n');
  const sysArr = [sys];
  if (profileCtx) sysArr.push('\nPROFILO INVESTITORE (leggi ogni campo con attenzione):\n' + profileCtx);

  try {
    const data = await fetchWithRetry(API_BASE + '/api/claude', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', ...getAuthHeaders() },
      body: JSON.stringify({
        model: MODEL_HAIKU,
        // 6144: il dossier ha piu' sezioni del vecchio report. Non alzare oltre:
        // il Worker ha ~85s prima del 524 di Cloudflare (vedi CLAUDE.md).
        max_tokens: 6144,
        temperature: 0.5,
        system: sysArr.join('\n'),
        messages: [{ role: 'user', content: prompt }],
        tools: [{ type: 'web_search_20250305', name: 'web_search' }],
      }),
    });

    if (data.error) {
      trackClaude({}, false, true);
      return { error: data.error.message || data.error || 'Errore API Claude' };
    }

    const usage = data.usage || { input_tokens: 0, output_tokens: 0 };
    const hasWebSearch = (data.content || []).some(
      b => b.type === 'tool_use' || b.type === 'web_search_tool_result'
    );
    trackClaude(usage, hasWebSearch, false);

    const rawText = (data.content || [])
      .filter(b => b.type === 'text')
      .map(b => b.text)
      .join('\n');

    const recoveryData = parseRecoveryData(rawText);
    const text = stripRecoveryData(rawText);

    // If Worker fell back to a cheaper model due to rate limits, flag it
    const fallbackModel = data._fallbackModel || null;

    return { text: text || 'Nessuna risposta ricevuta.', usage, recoveryData, fallbackModel };
  } catch (e) {
    trackClaude({}, false, true);
    return { error: e.message || 'Errore di connessione a Claude' };
  }
}

// ── Intelligence Chat (follow-up questions on report) ────
// OPT-4: Report as message pair (not system prompt) — saves ~5K tokens/msg
// OPT-10: Compact portfolio format (same as Intelligence report) — ~40% fewer tokens
// OPT-11: Reduced max_tokens 2048→1024 + concise instruction — ~50% shorter responses
export async function callIntelligenceChat(messages, profileCtx, stocks, funds, reportText, quotes) {
  // OPT-10: Use compact format (with netInvested) instead of full verbose format
  const compactPortfolio = fmtCompactPortfolio(stocks, funds, quotes);

  // Compact system prompt — role + concise instruction
  const sys = [
    `DATA DI OGGI: ${todayIT()}.`,
    'Sei il Portfolio Intelligence Agent — analista senior per patrimoni diversificati. Italiano. Conciso, pragmatico, diretto.',
    'Stai rispondendo a domande su un DOSSIER ISTRUTTORIO destinato a un colloquio con un consulente umano: fornisci elementi oggettivi e controtesi, non istruzioni operative.',
    'INTEGRITA\' DEI DATI (prevale su tutto): non inventare mai un numero assente dai dati forniti — scrivi "dati insufficienti" e di\' quale dato serve. Non dedurre identita\' o settore di un\'azienda dal solo ticker. Non sommare valute diverse. Non ricalcolare le percentuali ne\' i FATTI STRUTTURALI: usali come dati. Marca le affermazioni non banali con [confidenza: alta|media|bassa].',
  ];
  if (profileCtx) sys.push('\nPROFILO INVESTITORE:\n' + profileCtx);
  // OPT-11: Concise instruction + web search for trend/geopolitical analysis
  sys.push('\nRispondi in modo conciso e mirato (max 500 parole). Usa web_search quando l\'utente chiede trend, outlook, analisi macro, geopolitiche o aggiornamenti di mercato. Usa tabelle markdown quando utile.');

  // OPT-4: Prepend report+portfolio as user/assistant pair (sent once, not repeated in system)
  // OPT-10: Portfolio in compact pipe-separated format
  const contextMessages = [];
  if (reportText) {
    contextMessages.push({
      role: 'user',
      content: `Ecco il mio patrimonio (formato compatto, separatore |), i fatti strutturali calcolati dall'app e il tuo dossier.\n\nPATRIMONIO:\n${compactPortfolio}\n\n${fmtStructuralFacts(stocks, funds, quotes)}\n\nDOSSIER:\n${reportText}`
    });
    contextMessages.push({
      role: 'assistant',
      content: 'Ho il report e il patrimonio nel contesto. Sono pronto a rispondere alle tue domande di approfondimento.'
    });
  }

  try {
    const data = await fetchWithRetry(API_BASE + '/api/claude', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', ...getAuthHeaders() },
      body: JSON.stringify({
        model: MODEL_HAIKU,
        max_tokens: 2048,
        temperature: 0.7,
        system: sys.join('\n'),
        messages: [...contextMessages, ...messages],
        tools: [{ type: 'web_search_20250305', name: 'web_search' }],
      }),
    });

    if (data.error) {
      trackClaude({}, false, true);
      return { error: data.error.message || data.error || 'Errore API Claude' };
    }

    const usage = data.usage || { input_tokens: 0, output_tokens: 0 };
    const hasWebSearch = (data.content || []).some(
      b => b.type === 'tool_use' || b.type === 'web_search_tool_result'
    );
    trackClaude(usage, hasWebSearch, false);

    const text = (data.content || [])
      .filter(b => b.type === 'text')
      .map(b => b.text)
      .join('\n');

    return { text: text || 'Nessuna risposta ricevuta.', usage };
  } catch (e) {
    trackClaude({}, false, true);
    return { error: e.message || 'Errore di connessione a Claude' };
  }
}
